/**
 * Компонент таблицы свёрнутых (валидированных) полей.
 * Отображает поля в табличном формате без собственного заголовка (для использования внутри AccordionSection).
 */

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Pencil, X, Cake, type LucideIcon } from "lucide-react";
import { cn } from "@/utils";

/** Кнопка-действие в строке: иконка + dark-pill tooltip через Portal (не клипится контейнером). */
const RowAction: React.FC<{
  icon: LucideIcon;
  tooltip: string;
  onClick: () => void;
  danger?: boolean;
}> = ({ icon: Icon, tooltip, onClick, danger }) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    if (hover && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ left: r.left + r.width / 2, top: r.bottom + 6 });
    }
  }, [hover]);

  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={cn(
          "grid place-items-center w-7 h-7 rounded-md transition-colors",
          "text-ink-3 hover:bg-surface-elevated",
          danger ? "hover:text-red-600" : "hover:text-ink-1",
        )}
      >
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </button>
      {hover &&
        pos &&
        createPortal(
          <span
            className={cn(
              "fixed z-[100] -translate-x-1/2 whitespace-nowrap pointer-events-none",
              "px-2 py-0.5 rounded-md bg-zinc-900 text-white text-fs-2xs font-medium shadow-shell-md",
            )}
            style={{ left: pos.left, top: pos.top }}
          >
            {tooltip}
          </span>,
          document.body,
        )}
    </>
  );
};

export interface CollapsedFieldData {
  /** Уникальный идентификатор поля */
  id: string;
  /** Метка поля (например, "Полное имя") */
  label: string;
  /** Основное значение */
  value: string;
  /** Дополнительное значение (например, возраст) */
  suffix?: string;
  /** Иконка для suffix (например, Cake для возраста) */
  suffixIcon?: React.ReactNode;
  /** Показывать ли иконки действий */
  showActions?: boolean;
  /** Layout: horizontal - label слева, value справа; vertical - label сверху, value снизу */
  layout?: "horizontal" | "vertical";
  /** Статус валидации: valid - зелёная галочка, invalid - красный крестик */
  validationStatus?: "valid" | "invalid";
}

interface CollapsedFieldsTableProps {
  /** Данные полей для отображения */
  fields: CollapsedFieldData[];
  /** Обработчик редактирования поля */
  onEdit?: (fieldId: string) => void;
  /** Обработчик очистки поля */
  onClear?: (fieldId: string) => void;
  /** Дополнительный CSS класс */
  className?: string;
}

const CollapsedFieldsTable: React.FC<CollapsedFieldsTableProps> = ({ fields, onEdit, onClear, className }) => {
  if (fields.length === 0) {
    return null;
  }

  return (
    <div className={cn("rounded-lg border border-line bg-surface-elevated overflow-hidden", className)}>
      {fields.map((field, index) => {
        const isVertical = field.layout === "vertical";
        const isLast = index === fields.length - 1;

        return (
          <div
            key={field.id}
            className={cn(
              "group flex px-4 py-3 transition-colors",
              isVertical ? "flex-row items-start" : "items-center",
              !isLast && "border-b border-dashed border-line",
              "hover:bg-surface-hover",
            )}
          >
            {isVertical ? (
              /* Вертикальный layout: текст слева вертикально, кнопки справа */
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink-1">{field.label}</div>
                  {field.value && <div className="text-xs text-ink-3 mt-0.5">{field.value}</div>}
                </div>
              </>
            ) : (
              /* Горизонтальный layout: label слева, value справа */
              <>
                <div className="w-36 flex-shrink-0">
                  <span className="text-sm text-ink-3">{field.label}</span>
                </div>
                <div className="flex-1 flex items-center gap-3 min-w-0">
                  <span className="text-sm font-medium text-ink-1">{field.value}</span>
                  {field.suffix && (
                    <span className="flex items-center gap-1 text-sm text-ink-3">
                      {field.suffixIcon}
                      {field.suffix}
                    </span>
                  )}
                </div>
              </>
            )}

            {/* Edit + Clear: появляются только на hover ряда. Status-иконка не нужна —
                раз поле добавлено в свёрнутом виде, оно уже валидно. */}
            {field.showActions !== false && (
              <div
                className={cn(
                  "flex items-center gap-0.5 flex-shrink-0 ml-4 transition-opacity",
                  "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100",
                )}
              >
                {onEdit && <RowAction icon={Pencil} tooltip="Изменить" onClick={() => onEdit(field.id)} />}
                {onClear && <RowAction icon={X} tooltip="Удалить" onClick={() => onClear(field.id)} danger />}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CollapsedFieldsTable;
export { Cake };
